import { Link, useNavigate } from "@tanstack/react-router";
import {
	Check,
	Copy,
	ExternalLink,
	Plus,
	RefreshCw,
	Trash2,
} from "lucide-react";
import { useId, useMemo, useState } from "react";
import { AppInstallButton } from "@/components/connectors/app-install";
import { ConnectionShareSwitch } from "@/components/connectors/connection-share-switch";
import { PageSection } from "@/components/connectors/page-section";
import { ConnectionSetupChoices } from "@/components/connectors/setup-options";
import { CAPABILITY_LABELS } from "@/components/devices/device-display";
import { DeviceSelectItem } from "@/components/devices/device-select-item";
import { Button } from "@/components/ui/button";
import { FormDraftConflict } from "@/components/ui/form-draft-conflict";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { useFormDraft } from "@/hooks/use-form-draft";
import { useMembers } from "@/hooks/use-members";
import {
	type ConnectionItem,
	type ConnectorDefinitionItem,
	useAuthProfiles,
	useAvailableOperations,
	useDeleteConnection,
	useDevices,
	useUpdateConnection,
} from "@/lib/api";
import { useAuthState } from "@/lib/auth-state";
import { connectionWebhookUrl } from "@/lib/connection-webhook-url";
import { formatTimeAgo } from "@/lib/format-utils";
import { cn, FILLED_BOX } from "@/lib/utils";
import { ActionRunner } from "./action-runner";
import {
	getAppInstallMethod,
	getMethodLabel,
	getSelectedNoAuthMethod,
} from "./auth-helpers";
import { getAuthMethods } from "./auth-methods";
import { PolicySettingsLink } from "./connector-panels";
import {
	cleanConnectorValues,
	DynamicConnectorForm,
} from "./dynamic-connector-form";
import { ConnectionOAuthAuthorization } from "./oauth-permissions";

const SERVERLESS_DEVICE_VALUE = "__serverless__";
const ADD_DEVICE_VALUE = "__add_device__";

function useCanConfigureConnection(connection: ConnectionItem) {
	const { session } = useAuthState();
	return (
		connection.auth_profile_kind !== "oauth_account" ||
		Boolean(session?.user?.id && session.user.id === connection.created_by)
	);
}

export function ConnectionSettingsTab({
	connection,
	connectorDef,
	ownerSlug,
	showShare = true,
}: {
	connection: ConnectionItem;
	connectorDef?: ConnectorDefinitionItem;
	ownerSlug: string;
	/** False when the host already renders the share switch above the settings. */
	showShare?: boolean;
}) {
	const navigate = useNavigate();
	const displayNameId = useId();
	const updateConnection = useUpdateConnection();
	const deleteConnection = useDeleteConnection();
	const [displayName, setDisplayName] = useState(connection.display_name ?? "");
	const [confirmingDelete, setConfirmingDelete] = useState(false);

	const isDirty = displayName !== (connection.display_name ?? "");

	// A chat connection (live bot adapter) has no run-on-device target. Its
	// channels live in the Reach section rendered by the page.
	const isChat = connection.facets?.chat ?? false;

	const handleSave = () => {
		updateConnection.mutate({
			connection_id: connection.id,
			display_name: displayName.trim() || undefined,
		});
	};

	const handleDelete = () => {
		// The server suppresses device autowiring for the deleted binding.
		deleteConnection.mutate(connection.id, {
			onSuccess: () => {
				void navigate({
					to: "/$owner/connectors/$connectorKey",
					params: { owner: ownerSlug, connectorKey: connection.connector_key },
				});
			},
		});
	};

	return (
		<div className="space-y-6">
			<div className="space-y-3">
				<dl className="text-sm">
					<dt className="text-muted-foreground">Owner</dt>
					<dd>{connection.created_by_username ?? "Unknown owner"}</dd>
				</dl>
				{showShare && <ConnectionShareSwitch connection={connection} />}
			</div>
			<div className="space-y-2">
				<Label htmlFor={displayNameId}>Display name</Label>
				<div className="flex gap-2">
					<Input
						id={displayNameId}
						value={displayName}
						onChange={(e) => setDisplayName(e.target.value)}
						placeholder={connectorDef?.name ?? connection.connector_key}
						className="max-w-md"
					/>
					<Button
						onClick={handleSave}
						disabled={!isDirty || updateConnection.isPending}
					>
						{updateConnection.isPending ? (
							<>
								<Spinner size="lg" className="mr-2" />
								Saving...
							</>
						) : (
							"Save"
						)}
					</Button>
				</div>
			</div>

			{!isChat && (
				<RunOnSection
					connection={connection}
					connectorDef={connectorDef}
					ownerSlug={ownerSlug}
				/>
			)}

			{isChat && (
				<ChatConnectionSetup
					connection={connection}
					connectorDef={connectorDef}
				/>
			)}

			{/* Chat connections use bot/token fields below (or create flow), not
			    the data-connector auth-profile picker. */}
			{!isChat && (
				<AuthSection
					connection={connection}
					connectorDef={connectorDef}
					ownerSlug={ownerSlug}
				/>
			)}

			<div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
				<p className="text-sm font-medium">Delete this connection</p>
				<p className="mt-1 text-xs text-muted-foreground">
					{isChat
						? "Disconnects this connection. Past messages stay in your workspace."
						: "Removes the connection and stops its feeds. Past events stay in your workspace."}
				</p>
				<div className="mt-3">
					{!confirmingDelete ? (
						<Button
							variant="outline"
							size="sm"
							className="text-destructive hover:text-destructive"
							onClick={() => setConfirmingDelete(true)}
						>
							<Trash2 className="mr-1.5 h-3.5 w-3.5" />
							Delete connection
						</Button>
					) : (
						<div className="flex items-center gap-2">
							<Button
								variant="destructive"
								size="sm"
								disabled={deleteConnection.isPending}
								onClick={handleDelete}
							>
								{deleteConnection.isPending ? (
									<Spinner size="md" className="mr-1.5" />
								) : null}
								Confirm delete
							</Button>
							<Button
								variant="ghost"
								size="sm"
								onClick={() => setConfirmingDelete(false)}
								disabled={deleteConnection.isPending}
							>
								Cancel
							</Button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export function ChatConnectionSetup({
	connection,
	connectorDef,
}: {
	connection: ConnectionItem;
	connectorDef?: ConnectorDefinitionItem;
}) {
	const updateConnection = useUpdateConnection();
	const [copied, setCopied] = useState<string | null>(null);
	const webhookUrl = connectionWebhookUrl(connection.slug);
	const optionsSchema = connectorDef?.options_schema;
	// Only offer credential rotation when the schema actually declares fields to
	// rotate. An empty `{}` schema is truthy but renders no inputs, leaving a
	// permanently-disabled "Update credentials" button — with no inputs there is
	// nothing the operator can change, and rotation requires a change.
	const rotatableSchema =
		optionsSchema &&
		typeof optionsSchema === "object" &&
		Object.keys(
			(optionsSchema as { properties?: object }).properties ?? optionsSchema,
		).length > 0
			? optionsSchema
			: null;
	// Rotation is a partial update: seed no creation defaults or stored secrets.
	const credentialDraft = useFormDraft(
		connection.id,
		{} as Record<string, unknown>,
		rotatableSchema,
	);
	const { values: credentials, setValues: setCredentials } = credentialDraft;
	// Serializing a half-typed value throws, so an invalid draft rotates nothing
	// and the button stays disabled until the field parses.
	const rotatedCredentials = credentialDraft.hasInvalidInput
		? {}
		: cleanConnectorValues(credentials);
	const isManaged = connection.effective_credential_mode === "managed";
	// Scopes/events come from the catalog def's app_installation method
	// (SLACK_BOT_SCOPES / SLACK_BOT_EVENTS in the slack connector) — the single
	// source of truth; a hardcoded copy here drifted once already.
	const installMethod = (
		(connectorDef?.auth_schema?.methods as
			| Array<Record<string, unknown>>
			| undefined) ?? []
	).find((m) => m.type === "app_installation");
	const botScopes = (installMethod?.permissions as string[] | undefined) ?? [];
	const botEvents = (installMethod?.events as string[] | undefined) ?? [];
	const manifest =
		connection.connector_key === "slack" && botScopes.length > 0
			? JSON.stringify(
					{
						display_information: {
							name: connection.display_name ?? "Lobu bot",
						},
						features: {
							bot_user: {
								display_name: connection.display_name ?? "Lobu bot",
								always_online: true,
							},
							slash_commands: [
								{
									command: "/lobu",
									url: webhookUrl,
									description: "Talk to Lobu",
								},
							],
						},
						oauth_config: {
							scopes: { bot: botScopes },
						},
						settings: {
							event_subscriptions: {
								request_url: webhookUrl,
								bot_events: botEvents,
							},
							interactivity: { is_enabled: true, request_url: webhookUrl },
							socket_mode_enabled: false,
						},
					},
					null,
					2,
				)
			: null;

	const copy = async (key: string, value: string) => {
		await navigator.clipboard.writeText(value);
		setCopied(key);
		window.setTimeout(() => setCopied(null), 1500);
	};

	return (
		<div className="space-y-4">
			<div className="space-y-2">
				<Label>Webhook URL</Label>
				<div className="flex max-w-2xl gap-2">
					<Input value={webhookUrl} readOnly className="font-mono text-xs" />
					<Button
						variant="outline"
						size="icon"
						onClick={() => void copy("webhook", webhookUrl)}
					>
						{copied === "webhook" ? (
							<Check className="h-4 w-4" />
						) : (
							<Copy className="h-4 w-4" />
						)}
					</Button>
				</div>
				<p className="text-xs text-muted-foreground">
					Configure this as the provider's events, commands, and interactivity
					endpoint.
				</p>
			</div>

			{manifest ? (
				<div className="space-y-2">
					<Label>Slack app manifest</Label>
					<p className="text-xs text-muted-foreground">
						Create a Slack app from this manifest, or copy it into Slack's app
						manifest editor to configure events, scopes, commands, and
						interactivity together. Then install the app and paste its bot token
						+ signing secret back into the connection credentials.
					</p>
					<div className="flex gap-2">
						<Button asChild variant="outline" size="sm">
							<a
								href={`https://api.slack.com/apps?new_app=1&manifest_json=${encodeURIComponent(manifest)}`}
								target="_blank"
								rel="noreferrer"
							>
								<ExternalLink className="mr-1.5 h-3.5 w-3.5" />
								Create app in Slack
							</a>
						</Button>
						<Button
							variant="outline"
							size="sm"
							onClick={() => void copy("manifest", manifest)}
						>
							{copied === "manifest" ? (
								<Check className="mr-1.5 h-3.5 w-3.5" />
							) : (
								<Copy className="mr-1.5 h-3.5 w-3.5" />
							)}
							Copy manifest
						</Button>
					</div>
				</div>
			) : null}

			{!isManaged && credentialDraft.revision ? (
				<div className="space-y-3">
					<div>
						<Label>Rotate credentials</Label>
						<p className="mt-1 text-xs text-muted-foreground">
							Enter only values you want to replace. Stored secrets are never
							shown here.
						</p>
					</div>
					<DynamicConnectorForm
						schema={credentialDraft.revision ?? undefined}
						values={credentials}
						onValuesChange={setCredentials}
					/>
					<FormDraftConflict
						visible={credentialDraft.hasConflict}
						onReload={credentialDraft.reset}
					/>
					<Button
						disabled={
							!credentialDraft.canSubmit ||
							Object.keys(rotatedCredentials).length === 0 ||
							updateConnection.isPending
						}
						onClick={() =>
							updateConnection.mutate({
								connection_id: connection.id,
								config: rotatedCredentials,
							})
						}
					>
						{updateConnection.isPending ? (
							<Spinner size="md" className="mr-2" />
						) : null}
						Update credentials
					</Button>
				</div>
			) : null}
		</div>
	);
}

function RunOnSection({
	connection,
	connectorDef,
	ownerSlug,
}: {
	connection: ConnectionItem;
	connectorDef?: ConnectorDefinitionItem;
	ownerSlug: string;
}) {
	const navigate = useNavigate();
	const canConfigure = useCanConfigureConnection(connection);
	const updateConnection = useUpdateConnection();
	const {
		data: devices = [],
		refetch: refetchDevices,
		isFetching: devicesFetching,
	} = useDevices();

	const requiredCapability = connectorDef?.required_capability ?? null;
	const currentDeviceId = connection.device_worker_id;

	const deviceOptions = useMemo(() => {
		const base = devices.filter(
			(d) =>
				d.organization_id === connection.organization_id &&
				(!requiredCapability || d.capabilities.includes(requiredCapability)),
		);
		// Make the connection's current device selectable even if /api/me/devices
		// no longer returns it (moved or dropped off) — otherwise the Select renders blank.
		if (currentDeviceId && !base.some((d) => d.id === currentDeviceId)) {
			return [
				{
					id: currentDeviceId,
					worker_id: connection.device_worker_handle ?? currentDeviceId,
					platform: connection.device_platform ?? null,
					app_version: null,
					capabilities: [] as string[],
					label: connection.device_label ?? null,
					last_seen_at: "",
					online: connection.device_online ?? false,
					organization_id: connection.organization_id,
					organization_name: null,
					organization_slug: null,
					connector_count: 0,
					connector_error_count: 0,
					last_sync_at: null,
					connectors: [],
					update_required: [],
				},
				...base,
			];
		}
		return base;
	}, [devices, requiredCapability, connection, currentDeviceId]);

	const handleChange = (value: string) => {
		if (value === ADD_DEVICE_VALUE) {
			void navigate({
				to: "/$owner/connectors/create",
				params: { owner: ownerSlug },
			});
			return;
		}
		const next = value === SERVERLESS_DEVICE_VALUE ? null : value;
		if (next === (currentDeviceId ?? null)) return;
		updateConnection.mutate({
			connection_id: connection.id,
			device_worker_id: next,
		});
	};

	const selectValue =
		currentDeviceId ?? (requiredCapability ? "" : SERVERLESS_DEVICE_VALUE);

	return (
		<div className="space-y-2">
			<div className="flex items-center gap-2">
				<Label>
					Run on
					{requiredCapability ? (
						<span className="text-destructive ml-1">*</span>
					) : null}
				</Label>
				{updateConnection.isPending ? (
					<span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
						<Spinner size="sm" />
						Saving…
					</span>
				) : null}
			</div>
			<div className="flex items-center gap-2 max-w-md">
				<Select
					value={selectValue}
					onValueChange={handleChange}
					disabled={!canConfigure || updateConnection.isPending}
				>
					<SelectTrigger className="h-8 flex-1">
						<SelectValue placeholder="Select a device" />
					</SelectTrigger>
					<SelectContent>
						{!requiredCapability && (
							<SelectItem value={SERVERLESS_DEVICE_VALUE}>
								Serverless (default)
							</SelectItem>
						)}
						{deviceOptions.map((device) => (
							<DeviceSelectItem key={device.id} device={device} />
						))}
						<SelectItem value={ADD_DEVICE_VALUE}>
							<span className="flex items-center gap-2 text-muted-foreground">
								<Plus className="h-3.5 w-3.5" />
								Add device
							</span>
						</SelectItem>
					</SelectContent>
				</Select>
				<button
					type="button"
					onClick={() => void refetchDevices()}
					disabled={devicesFetching}
					className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50"
				>
					<RefreshCw
						className={`h-3 w-3 ${devicesFetching ? "animate-spin" : ""}`}
					/>
					Refresh
				</button>
			</div>
			{requiredCapability && deviceOptions.length === 0 ? (
				<p className="text-xs text-muted-foreground">
					No device has granted the “
					{CAPABILITY_LABELS[requiredCapability] ?? requiredCapability}”
					permission. Open the Lobu app on a device and grant it, then{" "}
					<button
						type="button"
						onClick={() => void refetchDevices()}
						className="underline hover:text-foreground"
					>
						refresh
					</button>
					.
				</p>
			) : currentDeviceId && connection.device_online === false ? (
				<p className="text-xs text-muted-foreground">
					Device offline — sync runs wait for it to check in
					{connection.device_last_seen_at
						? ` (last seen ${formatTimeAgo(connection.device_last_seen_at)})`
						: ""}
					.
				</p>
			) : null}
		</div>
	);
}

const AUTH_KIND_LABEL: Record<
	NonNullable<ConnectionItem["auth_profile_kind"]>,
	string
> = {
	env: "Environment variable",
	oauth_app: "OAuth app (workspace-wide)",
	oauth_account: "OAuth account",
	browser_session: "Browser session (local)",
	interactive: "Interactive credential",
};

const AUTH_KIND_BLURB: Record<
	NonNullable<ConnectionItem["auth_profile_kind"]>,
	string
> = {
	env: "A secret pulled from the worker process env. Update by changing the env var on whichever device runs this connector.",
	oauth_app:
		"Shared client configuration managed by workspace administrators. Account owners grant consent separately.",
	oauth_account:
		"The account owner can reconnect to renew access or grant additional permissions.",
	browser_session:
		"Reuses your local browser cookies on a device. Sign in again using the browser pairing flow on that device.",
	interactive:
		"A credential the agent fills in at run-time (e.g. a one-time code). Nothing stored long-term.",
};

function ProfileRow({
	heading,
	name,
	kind,
	status,
	isDefault,
	headerRight,
	footer,
}: {
	heading: string;
	name: string;
	kind: NonNullable<ConnectionItem["auth_profile_kind"]> | null;
	status: string | null;
	isDefault?: boolean;
	headerRight?: React.ReactNode;
	footer?: React.ReactNode;
}) {
	const kindLabel = kind ? AUTH_KIND_LABEL[kind] : null;
	const kindBlurb = kind ? AUTH_KIND_BLURB[kind] : null;
	return (
		<div className={cn(FILLED_BOX, "space-y-1.5 p-4")}>
			<div className="flex items-center justify-between gap-2">
				<p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
					{heading}
				</p>
				{headerRight}
			</div>
			<div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
				<span className="text-sm font-medium">{name}</span>
				{kindLabel ? (
					<span className="text-xs text-muted-foreground">· {kindLabel}</span>
				) : null}
				{status ? (
					<span
						className={
							status === "active"
								? "text-xs text-green-500"
								: "text-xs text-amber-500"
						}
					>
						· {status}
					</span>
				) : null}
				{isDefault ? (
					<span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
						Org default
					</span>
				) : null}
			</div>
			{kindBlurb ? (
				<p className="text-xs text-muted-foreground">{kindBlurb}</p>
			) : null}
			{footer}
		</div>
	);
}

export function AuthSection({
	connection,
	connectorDef,
	ownerSlug,
}: {
	connection: ConnectionItem;
	connectorDef?: ConnectorDefinitionItem;
	ownerSlug: string;
}) {
	// Connections whose connector authenticates via an App installation (e.g.
	// GitHub) re-authorize by re-running the gateway install flow in recovery
	// mode (re-binds a stuck/expired installation), not via the generic
	// OAuth account path. Driven by the connector's auth_schema, so no
	// provider is special-cased here.
	const appInstall = getAppInstallMethod(connectorDef?.auth_schema);
	const hasOAuth = getAuthMethods(connectorDef?.auth_schema).some(
		(method) => method.type === "oauth",
	);

	const managedBy = connection.config?.managedBy;
	if (
		managedBy &&
		typeof managedBy === "object" &&
		"org" in managedBy &&
		typeof managedBy.org === "string"
	)
		return (
			<div className="space-y-2">
				<Label>Auth</Label>
				<p className="text-sm text-muted-foreground">
					Managed authorization from {managedBy.org}. This runtime uses its
					cloud login to access your grant.
				</p>
			</div>
		);
	const noAuthMethod = getSelectedNoAuthMethod(
		connection,
		connectorDef?.auth_schema,
	);
	if (noAuthMethod) {
		return (
			<div id="connection-auth" className="space-y-2 scroll-mt-6">
				<Label>Auth</Label>
				<p className="text-sm text-muted-foreground">
					{getMethodLabel(noAuthMethod)}
				</p>
			</div>
		);
	}
	if (!connection.auth_profile_name) {
		return (
			<div id="connection-auth" className="space-y-2 scroll-mt-6">
				<ConnectionSetupChoices
					organizationId={connection.organization_id}
					connectorKey={connection.connector_key}
				/>
				<Label>Auth</Label>
				{appInstall ? (
					<div className="space-y-2">
						<p className="text-sm text-muted-foreground">
							No {connectorDef?.name ? `${connectorDef.name} ` : ""}app
							installation linked yet.
						</p>
						<AppInstallButton
							organizationId={connection.organization_id}
							connectorKey={connection.connector_key}
							providerLabel={connectorDef?.name}
							installShape={appInstall.installShape}
							recovery
							variant="outline"
						/>
					</div>
				) : (
					<div className="space-y-2">
						<p className="text-sm text-muted-foreground">
							{hasOAuth
								? "No account is connected yet."
								: "No auth profile attached."}
						</p>
						{hasOAuth && connectorDef && (
							<ConnectionOAuthAuthorization
								connection={connection}
								connectorDef={connectorDef}
							/>
						)}
					</div>
				)}
			</div>
		);
	}

	const isAccountChain =
		connection.auth_profile_kind === "oauth_account" &&
		!!connection.app_auth_profile_name;

	const accountHeading = isAccountChain ? "Account" : "Profile";

	return (
		<div id="connection-auth" className="space-y-2 scroll-mt-6">
			<Label>Auth</Label>
			<div className="space-y-2">
				{isAccountChain && connection.app_auth_profile_name ? (
					<AppProfileRow connection={connection} ownerSlug={ownerSlug} />
				) : null}
				<ProfileRow
					heading={accountHeading}
					name={connection.auth_profile_name}
					kind={connection.auth_profile_kind}
					status={connection.auth_profile_status}
					footer={
						connection.auth_profile_kind === "oauth_account" && connectorDef ? (
							<ConnectionOAuthAuthorization
								connection={connection}
								connectorDef={connectorDef}
							/>
						) : undefined
					}
				/>
				{connectorDef?.key ? (
					<p className="text-xs text-muted-foreground">
						Connector slug:{" "}
						<code className="font-mono">{connectorDef.key}</code>
					</p>
				) : null}
				{appInstall ? (
					<div className="space-y-1.5 pt-1">
						<ConnectionSetupChoices
							organizationId={connection.organization_id}
							connectorKey={connection.connector_key}
						/>
						<AppInstallButton
							organizationId={connection.organization_id}
							connectorKey={connection.connector_key}
							providerLabel={connectorDef?.name}
							installShape={appInstall.installShape}
							recovery
							variant="outline"
						/>
					</div>
				) : null}
			</div>
		</div>
	);
}

function AppProfileRow({
	connection,
	ownerSlug,
}: {
	connection: ConnectionItem;
	ownerSlug: string;
}) {
	const { isAdmin } = useMembers(connection.organization_id);
	const { data: profiles = [] } = useAuthProfiles(connection.organization_id, {
		connectorKey: connection.connector_key,
		profileKind: "oauth_app",
	});

	const currentSlug = connection.app_auth_profile_slug ?? undefined;
	const currentProfile = profiles.find((p) => p.slug === currentSlug) ?? null;
	const isDefault =
		!!currentProfile?.is_default_for_connector &&
		currentProfile.connector_key === connection.connector_key;

	return (
		<ProfileRow
			heading="Workspace app"
			name={connection.app_auth_profile_name ?? "—"}
			kind={connection.app_auth_profile_kind ?? "oauth_app"}
			status={connection.app_auth_profile_status}
			isDefault={isDefault}
			headerRight={
				isAdmin ? (
					<Link
						to="/$owner/connectors/$connectorKey"
						params={{
							owner: ownerSlug,
							connectorKey: connection.connector_key,
						}}
						search={{ setup: "oauth_app" }}
						className="text-xs text-muted-foreground hover:text-foreground"
					>
						Manage app
					</Link>
				) : null
			}
			footer={
				isAdmin ? (
					<p className="text-xs text-muted-foreground">
						This account stays linked to the app used to authorize it. To use
						another app, connect a new account.
					</p>
				) : undefined
			}
		/>
	);
}

export function ConnectionActionsSection({
	connection,
	ownerSlug,
	inPeek = false,
	onReviewPermissions,
}: {
	connection: ConnectionItem;
	ownerSlug?: string;
	inPeek?: boolean;
	onReviewPermissions?: () => void;
}) {
	const {
		data: operations = [],
		isLoading,
		isError,
	} = useAvailableOperations(connection.id);
	if (isLoading)
		return <p className="text-sm text-muted-foreground">Loading actions…</p>;
	if (isError)
		return (
			<p role="alert" className="text-sm">
				Could not load actions. Close the pane and try again.
			</p>
		);
	if (operations.length === 0)
		return (
			<p className="text-sm text-muted-foreground">No actions available.</p>
		);
	const content = (
		<div className="space-y-3">
			<p className="text-xs text-muted-foreground">
				Approval is managed in organization settings.{" "}
				<PolicySettingsLink
					ownerSlug={ownerSlug}
					connectorKey={connection.connector_key}
					connectionId={connection.id}
				/>
			</p>
			<ActionRunner
				connectionId={connection.id}
				connectorKey={connection.connector_key}
				ownerSlug={ownerSlug}
				browse={inPeek}
				onReviewPermissions={onReviewPermissions}
				policyOperations={operations}
			/>
		</div>
	);
	return inPeek ? (
		content
	) : (
		<PageSection
			title="Actions"
			count={`${operations.length} ${operations.length === 1 ? "action" : "actions"}`}
		>
			{content}
		</PageSection>
	);
}
